/**
 * The pure half of the search in Sessions (recherche-sessions.md §3 and §5).
 *
 * ONE implementation, played by the server to retain the sessions and by the
 * screen to highlight what it found: two spellings of « grece égale Grèce »
 * would end up disagreeing about what they underline (§3, last paragraph).
 *
 * Nothing here touches a database, a clock or the DOM. Positions handed back
 * are UTF-16 indices into the ORIGINAL string (the one a `<mark>` is cut from),
 * never into the folded one: folding changes lengths (`œ` becomes two letters,
 * a letter plus its accent mark becomes one), so every match goes through a
 * folded-index to original-index table instead of comparing positions of two
 * different strings.
 */

/** Longest search typed, in characters; the input stops there (§3, H7). */
export const SEARCH_QUERY_MAX = 200;
/** Most words kept in one search; the ones after the tenth are ignored (§3, H17). */
export const SEARCH_TERMS_MAX = 10;
/** Shortest word that arms a search (the design system's rule, §3, H6). */
export const SEARCH_TERM_MIN = 2;
/** Most places a retained line lists, whatever the total (§8). */
export const SEARCH_HITS_MAX = 20;
/** Longest excerpt of a « trouvé dans » line, ellipses included (§5). */
export const SEARCH_EXCERPT_MAX = 80;

/** A passage of a text, `[start, end)`, in positions of THAT text. */
export type SearchSpan = readonly [number, number];

/** An excerpt as rendered and the passages of the search words inside it (§5). */
export type SearchExcerpt = {
  readonly text: string;
  /** Positions in `text`, ellipses included. */
  readonly spans: ReadonlyArray<SearchSpan>;
};

const MARKS = /\p{M}/gu;
const ELLIPSIS = '…';

/** Folds ONE code point: the whole fold is this, character by character. */
function foldChar(char: string): string {
  return char
    .normalize('NFD')
    .replace(MARKS, '')
    .toLowerCase()
    .replace(/œ/g, 'oe')
    .replace(/æ/g, 'ae')
    .replace(/[’‘ʼ]/g, "'");
}

/**
 * The fold of §3: Unicode decomposition (NFD), diacritical marks removed,
 * lower case, `œ` as `oe`, `æ` as `ae`, a typographic apostrophe as the
 * straight one. « grece » finds « Grèce », « coeur » finds « cœur ».
 *
 * Folded code point by code point, so the table of {@link findTermSpans} and
 * this function can never drift: the folded string IS the concatenation of the
 * per-character folds. (The one visible consequence is a final sigma, which
 * stays `σ` instead of `ς` - the same on both sides of a comparison.)
 */
export function foldSearchText(text: string): string {
  let folded = '';
  for (const char of text) folded += foldChar(char);
  return folded;
}

/**
 * Reads what an operator typed as the words to look for (§3).
 *
 * Trimmed, cut to {@link SEARCH_QUERY_MAX}, split on any whitespace, each word
 * folded. A word of fewer than {@link SEARCH_TERM_MIN} characters is ignored
 * (next to a longer one) and, when no word reaches the minimum, the search does
 * NOT arm: `null`. Words repeated are kept once; the ones past
 * {@link SEARCH_TERMS_MAX} are ignored. Punctuation is not a separator:
 * `marie.dupont` is one word.
 */
export function parseSearchQuery(raw: string): readonly string[] | null {
  const words = raw.trim().slice(0, SEARCH_QUERY_MAX).split(/\s+/);
  const terms: string[] = [];
  for (const word of words) {
    if (Array.from(word).length < SEARCH_TERM_MIN) continue;
    const folded = foldSearchText(word);
    if (folded === '' || terms.includes(folded)) continue;
    terms.push(folded);
    if (terms.length === SEARCH_TERMS_MAX) break;
  }
  return terms.length > 0 ? terms : null;
}

type FoldedText = {
  readonly folded: string;
  /** For each UTF-16 unit of `folded`, where its source character starts in the original. */
  readonly starts: number[];
  /** ... and where it ends (exclusive), marks that followed it included. */
  readonly ends: number[];
};

function foldWithMap(text: string): FoldedText {
  let folded = '';
  const starts: number[] = [];
  const ends: number[] = [];
  let index = 0;
  for (const char of text) {
    const next = index + char.length;
    const piece = foldChar(char);
    if (piece === '') {
      // A bare mark folds to nothing: the letter it follows owns it, so a
      // highlight of « e » covers the accent that came after it.
      if (ends.length > 0) ends[ends.length - 1] = next;
    } else {
      for (let k = 0; k < piece.length; k += 1) {
        starts.push(index);
        ends.push(next);
      }
      folded += piece;
    }
    index = next;
  }
  return { folded, starts, ends };
}

/**
 * Where the folded words stand in `text` (§5): the `[start, end)` passages, in
 * positions of the ORIGINAL text, sorted, those that overlap or touch merged.
 *
 * A word is looked for as a substring anywhere (D4: « ann » finds « Jeanne »).
 * `terms` are folded words, as {@link parseSearchQuery} hands them back.
 */
export function findTermSpans(text: string, terms: readonly string[]): SearchSpan[] {
  const { folded, starts, ends } = foldWithMap(text);
  const raw: Array<[number, number]> = [];
  for (const term of terms) {
    if (term === '') continue;
    let at = folded.indexOf(term);
    while (at !== -1) {
      const start = starts[at];
      const end = ends[at + term.length - 1];
      if (start !== undefined && end !== undefined) raw.push([start, end]);
      at = folded.indexOf(term, at + 1);
    }
  }
  raw.sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  const merged: Array<[number, number]> = [];
  for (const [start, end] of raw) {
    const last = merged[merged.length - 1];
    if (last !== undefined && start <= last[1]) {
      if (end > last[1]) last[1] = end;
    } else {
      merged.push([start, end]);
    }
  }
  return merged;
}

/**
 * True when EVERY word is found in AT LEAST ONE of the texts - not
 * necessarily the same one (D4: « marie grèce » retains a session whose first
 * name is Marie and whose answer mentions Greece).
 */
export function matchesAllTerms(texts: readonly string[], terms: readonly string[]): boolean {
  const folded = texts.map(foldSearchText);
  return terms.every((term) => folded.some((text) => text.includes(term)));
}

const isSpace = (char: string | undefined): boolean => char !== undefined && /\s/.test(char);
const isLowSurrogate = (char: string | undefined): boolean =>
  char !== undefined && char >= '\uDC00' && char <= '\uDFFF';

/**
 * The excerpt of §5: at most `maxLength` characters (ellipses included) around
 * the FIRST passage of a search word, cut at word boundaries, with « … » on a
 * side the text was cut on. `null` when no word is found.
 *
 * `spans` are ALL the passages of the words inside the excerpt, in positions of
 * the rendered excerpt string, ellipses counted, so a screen can cut its
 * `<mark>`s from `text` directly. They are recomputed on the excerpt body
 * rather than clipped from the whole text: what is underlined is exactly what
 * a reader sees.
 *
 * A first passage longer than the room left is clipped; a word longer than the
 * room is cut mid-word rather than dropped. Whitespace inside the text (a
 * several-lines field) is kept as is: the screen collapses it.
 */
export function searchExcerpt(
  text: string,
  terms: readonly string[],
  maxLength: number = SEARCH_EXCERPT_MAX,
): SearchExcerpt | null {
  const first = findTermSpans(text, terms)[0];
  if (first === undefined) return null;
  if (text.length <= maxLength) {
    return { text, spans: findTermSpans(text, terms) };
  }

  const length = text.length;
  const [spanStart, rawEnd] = first;

  // A window of `room` characters centred on the first passage, pushed back
  // inside the text when it overruns an end.
  const place = (room: number): [number, number] => {
    const spanEnd = Math.min(rawEnd, spanStart + room);
    const pad = Math.floor((room - (spanEnd - spanStart)) / 2);
    let a = Math.max(0, spanStart - pad);
    const b = Math.min(length, a + room);
    a = Math.max(0, b - room);
    return [a, Math.min(length, a + room)];
  };

  let [a, b] = place(Math.max(1, maxLength - 2));
  const ellipses = (a > 0 ? 1 : 0) + (b < length ? 1 : 0);
  if (ellipses < 2) [a, b] = place(Math.max(1, maxLength - ellipses));

  // Snap to word boundaries, never past the first passage.
  if (a > 0 && !isSpace(text[a - 1]) && !isSpace(text[a])) {
    let p = a;
    while (p < length && !isSpace(text[p])) p += 1;
    while (p < length && isSpace(text[p])) p += 1;
    a = p <= spanStart ? p : spanStart;
  }
  if (b < length && !isSpace(text[b - 1]) && !isSpace(text[b])) {
    let q = b;
    while (q > 0 && !isSpace(text[q - 1])) q -= 1;
    const spanEnd = Math.min(rawEnd, b);
    b = q >= spanEnd && q > a ? q : b;
  }
  // Never cut a surrogate pair in two.
  if (a > 0 && isLowSurrogate(text[a])) a -= 1;
  if (b < length && isLowSurrogate(text[b])) b += 1;
  while (a < b && isSpace(text[a])) a += 1;
  while (b > a && isSpace(text[b - 1])) b -= 1;

  const body = text.slice(a, b);
  const lead = a > 0 ? ELLIPSIS : '';
  const tail = b < length ? ELLIPSIS : '';
  const spans = findTermSpans(body, terms).map(
    ([start, end]) => [start + lead.length, end + lead.length] as const,
  );
  return { text: `${lead}${body}${tail}`, spans };
}
